import { View } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { CardSurface } from "@louise/moon-ui";
import { HorizontalSnapCarousel } from "@src/packages/homepage/plan/components/HorizontalSnapCarousel";
import {
  AlertTriangle,
  BookOpen,
  ClipboardList,
  Clock,
  Info,
  type LucideIcon,
} from "lucide-react-native";
import { semanticColors } from "@louise/theme/tokens";
import type { ExplainerSectionId, StepExplainer } from "./stepExplainers";

const TEXT_MID = semanticColors.text.secondary;
const TEXT_LIGHT = semanticColors.text.tertiary;
const PANEL_MIN_HEIGHT = 284;

/** How each explainer section sits on its panel. */
const SECTION_PANELS: Record<
  ExplainerSectionId,
  {
    icon: LucideIcon;
    ordered?: boolean;
    paragraph?: boolean;
    marker?: "dot" | "warning";
  }
> = {
  what_happens: { icon: BookOpen, paragraph: true },
  before: { icon: ClipboardList, ordered: true },
  on_the_day: { icon: Info, marker: "dot" },
  after: { icon: Clock, marker: "dot" },
  // Every line here is a reason to call, so every line carries the same sign.
  when_to_call: { icon: AlertTriangle, marker: "warning" },
};

/**
 * A step explainer, in full: one panel per section, under the section's own
 * label, side by side. The treatment hub and Home both draw an explainer with
 * this, so the same step reads the same in both places.
 */
export function StepExplainerPanels({
  explainer,
  measureKey,
}: {
  explainer: StepExplainer;
  /** Changes when the content does, so the panel heights are measured again. */
  measureKey: string;
}) {
  if (explainer.sections.length === 0) return null;
  return (
    <HorizontalSnapCarousel
      items={explainer.sections}
      gap={10}
      fallbackCardWidth={244}
      getCardWidth={(containerWidth) =>
        Math.min(268, Math.max(226, containerWidth * 0.78))
      }
      keyExtractor={(section) => section.id}
      measureKey={measureKey}
      equalizeItemHeights
      contentContainerStyle={{ paddingLeft: 0, paddingRight: 0 }}
      renderItem={(section, cardWidth, _index, equalizedHeight) => (
        <ExplainerSectionPanel
          sectionId={section.id}
          label={section.label}
          items={section.items}
          width={cardWidth}
          equalizedHeight={equalizedHeight}
        />
      )}
    />
  );
}

function ExplainerSectionPanel({
  sectionId,
  label,
  items,
  width,
  equalizedHeight,
}: {
  sectionId: ExplainerSectionId;
  label: string;
  items: string[];
  width: number;
  equalizedHeight?: number;
}) {
  const panel = SECTION_PANELS[sectionId];
  const Icon = panel.icon;
  // A panel, not a second glass card: these sit inside the card that holds the
  // explainer, and each one is its own page of the carousel. The panels share
  // the tallest one's height as a minimum, never as a cap, so a long section
  // grows rather than being cut off.
  return (
    <CardSurface
      variant="panel"
      style={{
        width,
        minHeight: Math.max(equalizedHeight ?? 0, PANEL_MIN_HEIGHT),
        padding: 14,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          marginBottom: 12,
        }}
      >
        <Icon size={13} color={TEXT_LIGHT} strokeWidth={1.75} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 10,
            lineHeight: 13,
            color: TEXT_LIGHT,
            fontWeight: "800",
            letterSpacing: 0.4,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Text>
      </View>

      {panel.paragraph ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 18,
            fontWeight: "500",
            color: TEXT_MID,
          }}
        >
          {items.join("\n\n")}
        </Text>
      ) : (
        <View style={{ gap: 8 }}>
          {items.map((item, index) => (
            <ExplainerItemRow
              key={`${sectionId}-${index}`}
              text={item}
              index={panel.ordered ? index : undefined}
              marker={panel.marker ?? "dot"}
            />
          ))}
        </View>
      )}
    </CardSurface>
  );
}

function ExplainerItemRow({
  text,
  index,
  marker,
}: {
  text: string;
  index?: number;
  marker: "dot" | "warning";
}) {
  return (
    <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 8 }}>
      <View style={{ width: 16, alignItems: "center", paddingTop: 1 }}>
        {typeof index === "number" ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              lineHeight: 15,
              fontWeight: "800",
              color: TEXT_MID,
            }}
          >
            {index + 1}.
          </Text>
        ) : marker === "warning" ? (
          <AlertTriangle size={14} color={TEXT_MID} strokeWidth={1.7} />
        ) : (
          <View
            style={{
              width: 4,
              height: 4,
              borderRadius: 2,
              marginTop: 6,
              backgroundColor: TEXT_MID,
            }}
          />
        )}
      </View>
      <Text
        className="font-dm-sans"
        style={{
          flex: 1,
          fontSize: 12,
          lineHeight: 18,
          fontWeight: "500",
          color: TEXT_MID,
        }}
      >
        {text}
      </Text>
    </View>
  );
}
